import React, { useState } from "react";
import { Check, Copy, MessageSquareText } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { smsHref } from "@/lib/format";

const BTN = {
  primary: "bg-plum text-mist hover:bg-orchid",
  ghost: "border border-gold/50 text-gold hover:bg-gold/10",
  quiet: "text-mist/85 hover:text-gold hover:bg-white/5",
  danger: "border border-red-300/50 text-red-100 hover:bg-red-500/15",
};

export function Btn({ variant = "primary", className = "", ...p }: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: keyof typeof BTN }) {
  return (
    <button
      type="button"
      {...p}
      className={cn(
        "inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full px-6 text-[15px] font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold disabled:cursor-not-allowed disabled:opacity-50",
        BTN[variant], className)}
    />
  );
}

export const inputCls =
  "w-full min-h-[48px] rounded-md border border-gold/30 bg-ink px-4 text-[15px] text-mist placeholder:text-mist/50 transition-colors duration-200 focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/60";

export const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>((p, ref) => (
  <input ref={ref} {...p} className={cn(inputCls, p.className)} />
));

export const Select = ({ options, className = "", ...p }: React.SelectHTMLAttributes<HTMLSelectElement> & { options: { value: string; label: string }[] }) => (
  <select {...p} className={cn(inputCls, "pr-8", className)}>
    {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

export const Textarea = (p: React.TextareaHTMLAttributes<HTMLTextAreaElement>) => (
  <textarea {...p} className={cn(inputCls, "min-h-[110px] py-3", p.className)} />
);

export function Field({ label, hint, htmlFor, children, className = "" }: { label: string; hint?: string; htmlFor?: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <label htmlFor={htmlFor} className="text-[14px] font-medium text-mist/90">{label}</label>
      {children}
      {hint && <p className="text-[14px] text-mist/65">{hint}</p>}
    </div>
  );
}

export function Panel({ title, actions, children, className = "", ...rest }: { title?: string; actions?: React.ReactNode; children: React.ReactNode; className?: string } & React.HTMLAttributes<HTMLElement>) {
  return (
    <section {...rest} className={cn("rounded-md border border-gold/20 bg-aubergine/70 p-5 md:p-6", className)}>
      {(title || actions) && (
        <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
          {title && <h3 className="font-display text-lg text-gold">{title}</h3>}
          {actions}
        </header>
      )}
      {children}
    </section>
  );
}

const STATUS_TONE: Record<string, string> = {
  requested: "border-gold/60 text-gold",
  confirmed: "border-emerald-300/60 text-emerald-200",
  completed: "border-mist/40 text-mist/80",
  cancelled: "border-red-300/60 text-red-200",
  no_show: "border-red-300/60 text-red-200",
  declined: "border-red-300/60 text-red-200",
};

export const StatusBadge = ({ status, label }: { status: string; label: string }) => (
  <span data-testid={`status-badge-${status}`} className={cn("inline-flex items-center rounded-full border px-3 py-1 text-[14px]", STATUS_TONE[status] || "border-orchid/60 text-[#e2b8fa]")}>
    {label}
  </span>
);

export function MessageBox({ text, phone, testid }: { text: string; phone?: string; testid: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Message copied");
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div className="flex flex-col gap-3">
      <textarea readOnly value={text} data-testid={`${testid}-text`} className={cn(inputCls, "min-h-[170px] py-3 font-body text-[14px] leading-relaxed")} />
      <div className="flex flex-wrap gap-3">
        <Btn variant="ghost" onClick={copy} data-testid={`${testid}-copy-btn`}>
          {copied ? <Check size={18} /> : <Copy size={18} />} Copy message
        </Btn>
        {phone && (
          <a href={smsHref(phone, text)} data-testid={`${testid}-sms-link`}
            className="inline-flex min-h-[48px] items-center gap-2 rounded-full bg-plum px-6 text-[15px] font-medium text-mist transition-colors duration-200 hover:bg-orchid">
            <MessageSquareText size={18} /> Open text message
          </a>
        )}
      </div>
    </div>
  );
}

export const ErrorNote = ({ msg }: { msg?: string }) =>
  msg ? <p role="alert" data-testid="form-error" className="rounded-md border border-red-300/40 bg-red-500/10 px-4 py-3 text-[14px] text-red-100">{msg}</p> : null;
